import React, { useRef, type ReactNode } from 'react';
import { ErrorMessage, HelperMessage, ValidMessage } from '@atlaskit/form';
import { Box } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import {
	FAILED_FIELD_STATE,
	PENDING_FIELD_STATE,
	RESOLVED_FIELD_STATE,
	STORED_FIELD_STATE,
} from '@atlassian/jira-forge-controllers-inline-fields-state/src/constants.tsx';
import { useInlineField } from '@atlassian/jira-forge-controllers-inline-fields-state/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { ForgeFieldDispatchButton } from './field-dispatch-button/index.tsx';
import messages from './messages.tsx';

type ForgeFieldMessageProps = {
	error: string | undefined;
	fieldId: string;
	renderFieldMessage: (error?: string) => ReactNode;
};

const ErrorView = ({ fieldId }: { fieldId: string }) => {
	const { formatMessage } = useIntl();

	return (
		<ErrorMessage>
			<Box paddingInlineEnd="space.050">{formatMessage(messages.forgeFieldErrorTextLabel)}</Box>
			<ForgeFieldDispatchButton fieldId={fieldId} />
		</ErrorMessage>
	);
};

const HelperView = () => {
	const { formatMessage } = useIntl();

	return (
		<HelperMessage>
			<Box paddingInlineEnd="space.050">
				<Spinner size="small" />
			</Box>
			{formatMessage(messages.forgeFieldPendingTextLabel)}
		</HelperMessage>
	);
};
const SuccessView = () => {
	const { formatMessage } = useIntl();

	return <ValidMessage>{formatMessage(messages.forgeFieldSuccessTextLabel)}</ValidMessage>;
};

export const ForgeFieldMessage = (props: ForgeFieldMessageProps) => {
	const failedErrorMessageAppeared = useRef(false);
	const { fieldId, renderFieldMessage, error } = props;
	const [fieldState] = useInlineField(fieldId);

	const renderDefaultErrorMessage = () => {
		if (renderFieldMessage !== null) {
			return renderFieldMessage(error);
		}
		return null;
	};

	if (error !== undefined) {
		return renderDefaultErrorMessage();
	}

	if (failedErrorMessageAppeared.current) {
		if (renderFieldMessage != null) {
			switch (fieldState?.status) {
				case FAILED_FIELD_STATE:
					return <ErrorView fieldId={fieldId} />;
				case STORED_FIELD_STATE:
					return <SuccessView />;
				case PENDING_FIELD_STATE:
				case RESOLVED_FIELD_STATE:
					return <HelperView />;
				default:
					return renderDefaultErrorMessage();
			}
		}
		return null;
	}

	if (fieldState?.status === FAILED_FIELD_STATE) {
		failedErrorMessageAppeared.current = true;
		return <ErrorView fieldId={fieldId} />;
	}

	return renderDefaultErrorMessage();
};
